import { FunctionComponent, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PersonIcon from '@mui/icons-material/Person';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import { savedApi, ApiError } from '../../../services/api';
import { useSavedItems, SavedItemType } from '../../../contexts/SavedItemsContext';
import CreateSavedListModal from '../../modals/CreateSavedListModal/CreateSavedListModal';
import styles from './SavedScreen.module.css';

type FilterType = 'all' | SavedItemType;

const SavedScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { savedItems, removeItem, saveItem } = useSavedItems();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ id: string; type: SavedItemType } | null>(null);
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  // Fetch lists from API on component mount
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const result = await savedApi.getLists();
        
        // Add API lists to saved items if they're not already there
        result.forEach((list: any) => {
          const existingList = savedItems.find(item => item.id === list.id && item.type === 'list');
          if (!existingList) {
            saveItem({
              id: list.id,
              type: 'list',
              title: list.name || list.title,
              subtitle: list.description || `${list.itemCount} items`,
              savedAt: new Date(list.lastUpdated),
              data: list
            });
          }
        });
      } catch (error) {
        console.error('Error fetching lists:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.message);
        }
      }
    };

    fetchLists();
  }, []);

  const filters: { type: FilterType; label: string; icon: JSX.Element }[] = [
    { type: 'all', label: 'All', icon: <BookmarkIcon /> },
    { type: 'provider', label: 'Providers', icon: <PersonIcon /> },
    { type: 'medication', label: 'Medications', icon: <MedicationIcon /> },
    { type: 'procedure', label: 'Procedures', icon: <MedicalServicesIcon /> },
    { type: 'care-plan', label: 'Care Plans', icon: <AutoAwesomeIcon /> },
    { type: 'list', label: 'Lists', icon: <FolderIcon /> }
  ];

  const filteredItems = useMemo(() => {
    let items = savedItems;

    // Filter by type
    if (activeFilter !== 'all') {
      items = items.filter(item => item.type === activeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.title.toLowerCase().includes(query) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(query))
      );
    }

    return items;
  }, [savedItems, activeFilter, searchQuery]);

  const handleItemClick = (item: any) => {
    switch (item.type) {
      case 'provider':
        navigate(`/provider/${item.id}`);
        break;
      case 'medication':
        navigate(`/medication/${item.id}`);
        break;
      case 'procedure':
        navigate(`/procedure/${item.id}`);
        break;
      case 'care-plan':
        navigate(`/ai-search-result-detail?id=${item.id}`);
        break;
      case 'list':
        navigate(`/list/${item.id}`);
        break;
    }
  };

  const handleDelete = (id: string, type: SavedItemType) => {
    setShowDeleteConfirm({ id, type });
  };

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      removeItem(showDeleteConfirm.id, showDeleteConfirm.type);
      setShowDeleteConfirm(null);
    }
  };

  const handleShare = async (item: any) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.title,
          text: item.subtitle || '',
          url: `${window.location.origin}/${item.type}/${item.id}`
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  const handleListCreated = (listId: string) => {
    // Navigate to the newly created list
    navigate(`/list/${listId}`);
  };

  const getItemIcon = (type: SavedItemType) => {
    switch (type) {
      case 'provider':
        return <PersonIcon className={styles.itemIcon} />;
      case 'medication':
        return <MedicationIcon className={styles.itemIcon} />;
      case 'procedure':
        return <MedicalServicesIcon className={styles.itemIcon} />;
      case 'care-plan':
        return <AutoAwesomeIcon className={styles.itemIcon} />;
      case 'list':
        return <FolderIcon className={styles.itemIcon} />;
      default:
        return <BookmarkIcon className={styles.itemIcon} />;
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className={styles.savedScreen}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Saved</h1>
        <button 
          className={styles.createListButton}
          onClick={() => setShowCreateListModal(true)}
        >
          <AddIcon />
          <span>Create List</span>
        </button>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.searchBar}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search saved items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.filterContainer}>
        <div className={styles.filterScroll}>
          {filters.map((filter) => (
            <button
              key={filter.type}
              className={`${styles.filterChip} ${activeFilter === filter.type ? styles.activeFilter : ''}`}
              onClick={() => setActiveFilter(filter.type)}
            >
              {filter.icon}
              <span>{filter.label}</span>
              {filter.type === 'all' && (
                <span className={styles.count}>{savedItems.length}</span>
              )}
              {filter.type !== 'all' && (
                <span className={styles.count}>
                  {savedItems.filter(item => item.type === filter.type).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.content}>
        {filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <BookmarkIcon className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>
              {searchQuery ? 'No results found' : 'No saved items'}
            </h3>
            <p className={styles.emptyText}>
              {searchQuery 
                ? 'Try adjusting your search or filters'
                : 'Items you save will appear here for quick access'
              }
            </p>
          </div>
        ) : (
          <div className={styles.itemsList}>
            {filteredItems.map((item) => (
              <div key={`${item.type}-${item.id}`} className={styles.savedItem}>
                <div 
                  className={styles.itemContent}
                  onClick={() => handleItemClick(item)}
                >
                  <div className={styles.itemIconContainer}>
                    {getItemIcon(item.type)}
                  </div>
                  <div className={styles.itemDetails}>
                    <h3 className={styles.itemTitle}>{item.title}</h3>
                    {item.subtitle && (
                      <p className={styles.itemSubtitle}>{item.subtitle}</p>
                    )}
                    <div className={styles.itemMeta}>
                      <AccessTimeIcon className={styles.timeIcon} />
                      <span className={styles.savedTime}>
                        Saved {formatDate(item.savedAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.itemActions}>
                  <button
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(item);
                    }}
                    aria-label="Share"
                  >
                    <ShareIcon />
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id, item.type);
                    }}
                    aria-label="Delete"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Remove saved item?</h3>
            <p className={styles.modalText}>
              This item will be removed from your saved list.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={confirmDelete}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateSavedListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={handleListCreated}
      />
    </div>
  );
};

export default SavedScreen;